// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { Message, Source, ThreadTask, WorkbenchHandoffArtifact } from '@/types';
import { teacherArtifactMarkdownFromArtifacts } from '@utils/teacherArtifacts';
import {
  attachLatestAssistantRag,
  attachLatestAssistantUi,
  insertClarificationChoiceBubbles,
  markLatestInteractiveAssistant,
} from '@/pages/main-chat-runtime/transcript/mainChatTranscriptReconciler';
import { hasInterruptResumedEvidence } from '@utils/clarificationChoice';
import { normalizeCapabilityRuntimeSources } from '@utils/messageCapabilityRuntime';

function toMessageRole(raw: unknown): Message['role'] | null {
  if (typeof raw !== 'string') return null;
  const role = raw.toLowerCase();
  if (role === 'user' || role === 'human') return 'user';
  if (role === 'assistant' || role === 'ai') return 'assistant';
  if (role === 'system') return 'system';
  return null;
}

function toMessageContent(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const text = (item as Record<string, unknown>).text;
          if (typeof text === 'string') return text;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n')
      .trim();
  }
  return '';
}

function normalizeCheckpointArtifacts(
  channelValues: Record<string, unknown> | null
): Record<string, unknown>[] {
  const artifactsRuntime = toPlainObject(channelValues?.artifacts_runtime);
  const merged: Record<string, unknown>[] = Array.isArray(channelValues?.artifacts)
    ? (channelValues!.artifacts.filter((item) => item && typeof item === 'object') as Record<
        string,
        unknown
      >[])
    : [];
  if (Array.isArray(artifactsRuntime?.artifacts)) {
    for (const item of artifactsRuntime.artifacts) {
      if (item && typeof item === 'object') {
        merged.push(item as Record<string, unknown>);
      }
    }
  }
  const seenIds = new Set(
    merged
      .map((item) => (typeof item.artifact_id === 'string' ? item.artifact_id : ''))
      .filter(Boolean)
  );

  for (const key of ['teacher_artifact', 'teacher_prediction_artifact'] as const) {
    const candidate = channelValues?.[key];
    if (!candidate || typeof candidate !== 'object') {
      continue;
    }
    const artifactId =
      typeof (candidate as Record<string, unknown>).artifact_id === 'string'
        ? String((candidate as Record<string, unknown>).artifact_id)
        : '';
    if (artifactId && seenIds.has(artifactId)) {
      continue;
    }
    if (artifactId) {
      seenIds.add(artifactId);
    }
    merged.push(candidate as Record<string, unknown>);
  }

  return merged;
}

function toUiBlocks(raw: unknown): Message['ui_blocks'] {
  return Array.isArray(raw) ? (raw as Message['ui_blocks']) : undefined;
}

function toPlainObject(raw: unknown): Record<string, unknown> | undefined {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : undefined;
}

function toSourceArray(raw: unknown): Source[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const sources = raw
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map((item): Source => {
      const content =
        typeof item.content === 'string'
          ? item.content
          : typeof item.content_preview === 'string'
            ? item.content_preview
            : '';
      return {
        title: typeof item.title === 'string' ? item.title : undefined,
        content,
        content_preview:
          typeof item.content_preview === 'string' ? item.content_preview : undefined,
        score: typeof item.score === 'number' ? item.score : undefined,
        source: typeof item.source === 'string' ? item.source : undefined,
        filename: typeof item.filename === 'string' ? item.filename : undefined,
        kb_id: typeof item.kb_id === 'string' ? item.kb_id : undefined,
        kb_name: typeof item.kb_name === 'string' ? item.kb_name : undefined,
        doc_id: typeof item.doc_id === 'string' ? item.doc_id : undefined,
        chunk_id: typeof item.chunk_id === 'string' ? item.chunk_id : undefined,
        retrieval_mode: typeof item.retrieval_mode === 'string' ? item.retrieval_mode : undefined,
        source_type: typeof item.source_type === 'string' ? item.source_type : undefined,
        metadata: toPlainObject(item.metadata),
      };
    });
  return sources.length > 0 ? sources : undefined;
}

function toOptionalString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
}

function toVisualizationState(raw: unknown): Message['visualization_state'] {
  return raw && typeof raw === 'object' ? (raw as Message['visualization_state']) : undefined;
}

function toUiGenerationMode(raw: unknown): Message['ui_generation_mode'] {
  return raw === 'tool' || raw === 'none' ? raw : undefined;
}

function toActionRevision(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.trunc(raw));
  }
  if (typeof raw === 'string' && raw.trim()) {
    const value = Number(raw);
    if (Number.isFinite(value)) {
      return Math.max(0, Math.trunc(value));
    }
  }
  return undefined;
}

function toFiniteNumber(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  }
  return undefined;
}

function extractHandoffArtifact(artifacts: Record<string, unknown>[]): WorkbenchHandoffArtifact | undefined {
  const found = artifacts
    .filter((a) => a.artifact_type === 'workbench_handoff')
    .pop();
  if (!found) return undefined;
  const payload = typeof found.payload === 'object' && found.payload !== null ? found.payload as Record<string, unknown> : {};
  return {
    artifact_type: 'workbench_handoff',
    schema_version: typeof found.schema_version === 'string' ? found.schema_version : typeof payload.schema_version === 'string' ? payload.schema_version : undefined,
    source_surface: typeof payload.source_surface === 'string' ? payload.source_surface : 'main_chat',
    target_surface: typeof payload.target_surface === 'string' ? payload.target_surface : 'teacher_workbench',
    seed_prompt: typeof payload.seed_prompt === 'string' ? payload.seed_prompt : '',
    context_artifact_ids: Array.isArray(payload.context_artifact_ids) ? payload.context_artifact_ids.filter((id): id is string => typeof id === 'string') : undefined,
    selected_skill_ids: Array.isArray(payload.selected_skill_ids) ? payload.selected_skill_ids.filter((id): id is string => typeof id === 'string') : undefined,
    class_context: payload.class_context && typeof payload.class_context === 'object' ? payload.class_context as Record<string, unknown> : undefined,
  };
}

function toFeedbackMap(raw: unknown): Record<string, 'up' | 'down'> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, 'up' | 'down'> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === 'up' || value === 'down') {
      out[String(key)] = value;
    }
  }
  return out;
}

export function normalizeThreadMessages(
  payload: unknown,
  threadId: string,
  options: { fallbackUserPrompt?: string } = {}
): Message[] {
  const data = payload as Record<string, any> | undefined;
  const directMessages = Array.isArray(data?.messages) ? data.messages : null;
  const snapshotMessages = Array.isArray(data?.snapshot?.messages)
    ? data.snapshot.messages
    : null;
  const direct = directMessages ?? snapshotMessages;
  const checkpointMessages = Array.isArray(data?.checkpoint?.channel_values?.messages)
    ? data.checkpoint.channel_values.messages
    : null;
  const preferredMessages =
    direct && direct.length > 0
      ? direct
      : checkpointMessages && checkpointMessages.length > 0
        ? checkpointMessages
        : direct ?? checkpointMessages ?? [];
  const rawMessages: unknown[] = preferredMessages;

  const messages = rawMessages
    .map((raw: unknown, index: number): Message | null => {
      const item = Array.isArray(raw) ? raw[0] : raw;
      if (!item || typeof item !== 'object') return null;
      const message = item as Record<string, unknown>;
      const kwargs =
        message.kwargs && typeof message.kwargs === 'object'
          ? (message.kwargs as Record<string, unknown>)
          : {};
      // H-9c: nested backend carriers, narrowed once instead of per-access
      // `as any` chains (all 23 sites below previously read through any).
      const additionalKwargs = toPlainObject(message.additional_kwargs);
      const messageData = toPlainObject(message.data);
      const responseMetadata = toPlainObject(message.response_metadata);
      const kwargsResponseMetadata = toPlainObject(kwargs.response_metadata);

      const role =
        toMessageRole(message.role) ??
        toMessageRole(message.type) ??
        toMessageRole(kwargs.role) ??
        toMessageRole(kwargs.type);
      if (!role) return null;

      const uiBlocks =
        toUiBlocks(message.ui_blocks) ??
        toUiBlocks(kwargs.ui_blocks) ??
        toUiBlocks(additionalKwargs?.ui_blocks);
      const visualizationState =
        toVisualizationState(message.visualization_state) ??
        toVisualizationState(kwargs.visualization_state) ??
        toVisualizationState(additionalKwargs?.visualization_state);
      const uiGenerationMode =
        toUiGenerationMode(message.ui_generation_mode) ??
        toUiGenerationMode(kwargs.ui_generation_mode) ??
        toUiGenerationMode(additionalKwargs?.ui_generation_mode);
      const thinkingTrace =
        toPlainObject(message.thinking_trace) ??
        toPlainObject(kwargs.thinking_trace) ??
        toPlainObject(additionalKwargs?.thinking_trace);
      const thinkingDurationSeconds =
        toFiniteNumber(message.thinking_duration_seconds) ??
        toFiniteNumber(kwargs.thinking_duration_seconds) ??
        toFiniteNumber(additionalKwargs?.thinking_duration_seconds);
      const actionRevision =
        toFiniteNumber(message.action_revision) ??
        toFiniteNumber(kwargs.action_revision) ??
        toFiniteNumber(additionalKwargs?.action_revision);
      const content =
        toMessageContent(message.content) ||
        toMessageContent(kwargs.content) ||
        toMessageContent(messageData?.content);
      if (!content && !(uiBlocks?.length || visualizationState)) return null;

      const timestamp =
        (typeof message.timestamp === 'string' && message.timestamp) ||
        (typeof message.created_at === 'string' && message.created_at) ||
        toOptionalString(responseMetadata?.created_at) ||
        toOptionalString(additionalKwargs?.created_at) ||
        toOptionalString(kwargsResponseMetadata?.created_at) ||
        new Date().toISOString();

      const id =
        (typeof message.id === 'string' && message.id) ||
        (typeof kwargs.id === 'string' && kwargs.id) ||
        `${threadId}-${index}`;
      const capabilityRuntime = normalizeCapabilityRuntimeSources(
        message.capability_runtime,
        message,
        kwargs,
        message.additional_kwargs
      );
      const sources =
        toSourceArray(message.sources) ??
        toSourceArray(message.rag_sources) ??
        toSourceArray(kwargs.sources) ??
        toSourceArray(kwargs.rag_sources) ??
        toSourceArray(additionalKwargs?.sources) ??
        toSourceArray(additionalKwargs?.rag_sources);
      const ragStatus =
        toOptionalString(message.rag_status) ??
        toOptionalString(kwargs.rag_status) ??
        toOptionalString(additionalKwargs?.rag_status);
      // INFRA-04：本轮联网检索执行状态（历史回放同源读取）。
      const webSearchStatus =
        toOptionalString(message.web_search_status) ??
        toOptionalString(kwargs.web_search_status) ??
        toOptionalString(additionalKwargs?.web_search_status);
      const retrievalMode =
        toOptionalString(message.retrieval_mode) ??
        toOptionalString(kwargs.retrieval_mode) ??
        toOptionalString(additionalKwargs?.retrieval_mode);

      return {
        id,
        role,
        content,
        timestamp,
        thread_id: threadId,
        capability_runtime: capabilityRuntime,
        sources,
        rag_status: ragStatus,
        web_search_status: webSearchStatus,
        retrieval_mode: retrievalMode,
        ui_blocks: uiBlocks,
        visualization_state: visualizationState,
        ui_generation_mode: uiGenerationMode,
        action_revision: actionRevision,
        thinking_trace: thinkingTrace as Message['thinking_trace'],
        thinking_duration_seconds: thinkingDurationSeconds,
      };
    })
    .filter((message: Message | null): message is Message => Boolean(message));

  const checkpointChannelValues =
    data?.checkpoint?.channel_values && typeof data.checkpoint.channel_values === 'object'
      ? (data.checkpoint.channel_values as Record<string, unknown>)
      : null;
  const checkpointUi = toPlainObject(checkpointChannelValues?.ui);
  const checkpointArtifactsRuntime = toPlainObject(checkpointChannelValues?.artifacts_runtime);
  const checkpointExecution = toPlainObject(checkpointChannelValues?.execution);
  const checkpointUiBlocks =
    toUiBlocks(checkpointChannelValues?.ui_blocks) ?? toUiBlocks(checkpointUi?.ui_blocks);
  const checkpointVisualizationState = toVisualizationState(
    checkpointChannelValues?.visualization_state ??
      checkpointArtifactsRuntime?.visualization_state
  );
  const checkpointUiGenerationMode = toUiGenerationMode(
    checkpointChannelValues?.ui_generation_mode ?? checkpointUi?.ui_generation_mode
  );
  const checkpointArtifacts = normalizeCheckpointArtifacts(checkpointChannelValues);
  // 权威位置是契约 ui 段（后端投影只保留分段、丢弃扁平键）；顶层扁平键仅作
  // legacy 兼容（历史 session event 曾以扁平形态落盘）。
  const checkpointFeedback = toFeedbackMap(
    checkpointUi?.message_feedback ?? checkpointChannelValues?.message_feedback
  );
  const checkpointRagSources = toSourceArray(checkpointChannelValues?.rag_sources);
  const checkpointRagStatus = toOptionalString(checkpointChannelValues?.rag_status);
  const checkpointRetrievalMode = toOptionalString(checkpointChannelValues?.retrieval_mode);
  const checkpointInteractiveMessageId =
    typeof checkpointChannelValues?.interactive_message_id === 'string'
      ? checkpointChannelValues.interactive_message_id
      : typeof checkpointUi?.interactive_message_id === 'string'
        ? checkpointUi.interactive_message_id
        : undefined;
  const checkpointActionRevision = toActionRevision(
    checkpointChannelValues?.ui_action_revision ??
      checkpointChannelValues?.action_revision ??
      checkpointUi?.ui_action_revision ??
      checkpointUi?.action_revision
  );

  const applyFeedback = (items: Message[]): Message[] =>
    items.map((message) => ({
      ...message,
      feedback: checkpointFeedback[message.id] ?? message.feedback,
    }));

  const hasAssistantMessage = messages.some((message) => message.role === 'assistant');
  const checkpointHandoff = extractHandoffArtifact(checkpointArtifacts);
  if (hasAssistantMessage) {
    const latestAssistantIdx = checkpointHandoff
      ? messages.findLastIndex((m) => m.role === 'assistant')
      : -1;
    const withHandoff = checkpointHandoff
      ? messages.map((message, index) =>
          index === latestAssistantIdx
            ? { ...message, handoff_artifact: checkpointHandoff }
            : message
        )
      : messages;
    // G7-N1：恢复路径与 live 投影同源派生用户澄清选择气泡（同一 helper、
    // 同 id 幂等）——checkpoint `ui.clarification_cards` 与 live 通道是同一
    // 卡片记录（finalize 原样拷贝进 ui_block payload），控制事件链作已答复
    // 对账信号。
    const withClarificationChoice = insertClarificationChoiceBubbles(
      withHandoff,
      {
        cards: Array.isArray(checkpointUi?.clarification_cards)
          ? (checkpointUi.clarification_cards as unknown[]).filter(
              (card) => card && typeof card === 'object'
            )
          : [],
        hasResumeEvidence: hasInterruptResumedEvidence(
          checkpointUi?.control_events ?? checkpointChannelValues?.control_events
        ),
        labels: { question: 'Q', answer: 'A' },
      }
    );
    return applyFeedback(
      attachLatestAssistantRag(
        attachLatestAssistantUi(
          withClarificationChoice,
          checkpointUiBlocks,
          checkpointVisualizationState,
          checkpointUiGenerationMode,
          checkpointInteractiveMessageId,
          checkpointActionRevision
        ),
        checkpointRagSources,
        checkpointRagStatus,
        checkpointRetrievalMode
      )
    );
  }
  const recoveredContent =
    (checkpointChannelValues && typeof checkpointChannelValues.solution === 'string'
      ? checkpointChannelValues.solution
      : typeof checkpointExecution?.solution === 'string'
        ? checkpointExecution.solution
      : '') || teacherArtifactMarkdownFromArtifacts(checkpointArtifacts);

  const hasRecoveredInteractiveSnapshot = Boolean(
    (checkpointUiBlocks && checkpointUiBlocks.length > 0) || checkpointVisualizationState
  );

  if (!recoveredContent && !hasRecoveredInteractiveSnapshot) {
    return messages;
  }

  const fallbackUserPrompt = String(options.fallbackUserPrompt || '').trim();
  const recoveredMessages =
    fallbackUserPrompt && messages.length === 0
      ? [
          {
            id: `${threadId}-recovered-user`,
            role: 'user' as const,
            content: fallbackUserPrompt,
            timestamp: new Date().toISOString(),
            thread_id: threadId,
          },
        ]
      : messages;

  return applyFeedback([
    ...markLatestInteractiveAssistant(recoveredMessages),
    {
      id: checkpointInteractiveMessageId || `${threadId}-recovered-assistant`,
      role: 'assistant',
      content: recoveredContent,
      timestamp: new Date().toISOString(),
      thread_id: threadId,
      capability_runtime: normalizeCapabilityRuntimeSources(
        checkpointChannelValues?.capability_runtime,
        checkpointChannelValues
      ),
      sources: checkpointRagSources,
      rag_status: checkpointRagStatus,
      retrieval_mode: checkpointRetrievalMode,
      ui_blocks: checkpointUiBlocks,
      visualization_state: checkpointVisualizationState,
      ui_generation_mode: checkpointUiGenerationMode,
      interactive: Boolean(checkpointUiBlocks?.length || checkpointVisualizationState),
      latest_snapshot: Boolean(checkpointUiBlocks?.length || checkpointVisualizationState),
      can_accept_action_patch: Boolean(checkpointUiBlocks?.length || checkpointVisualizationState),
      action_revision: checkpointActionRevision,
      handoff_artifact: checkpointHandoff,
    },
  ]);
}

export function normalizeThreadTasks(payload: unknown): ThreadTask[] {
  const rawTasks = Array.isArray((payload as { tasks?: unknown[] } | undefined)?.tasks)
    ? (payload as { tasks: unknown[] }).tasks
    : [];

  return rawTasks
    .filter((task): task is Record<string, unknown> => Boolean(task) && typeof task === 'object')
    .map((task) => ({
      task_id: typeof task.task_id === 'string' ? task.task_id : '',
      task_type: typeof task.task_type === 'string' ? task.task_type : '',
      status: typeof task.status === 'string' ? task.status : '',
      description: typeof task.description === 'string' ? task.description : '',
      thread_id: typeof task.thread_id === 'string' ? task.thread_id : '',
      user_id: typeof task.user_id === 'string' ? task.user_id : null,
      source_artifact_id:
        typeof task.source_artifact_id === 'string' ? task.source_artifact_id : '',
      output_artifact_ids: Array.isArray(task.output_artifact_ids)
        ? task.output_artifact_ids.filter((item): item is string => typeof item === 'string')
        : [],
      error_code: typeof task.error_code === 'string' ? task.error_code : null,
      created_at: typeof task.created_at === 'string' ? task.created_at : undefined,
      started_at: typeof task.started_at === 'string' ? task.started_at : null,
      completed_at: typeof task.completed_at === 'string' ? task.completed_at : null,
      notified: Boolean(task.notified),
      metadata:
        task.metadata && typeof task.metadata === 'object' && !Array.isArray(task.metadata)
          ? (task.metadata as Record<string, unknown>)
          : {},
    }))
    .filter((task) => task.task_id && task.thread_id);
}
